// Stop-frame tutorial (design §2.3 教学约束 v0.5.2, TECH_SPEC §4bis.15).
//
// The v0.5 tutorial was a hint pill on the intro overlay. Phone playtests were
// blunt about it: people read 「按住下滑，滑铲过杆」, nodded, and then jumped
// into the first bar anyway. Reading a verb is not learning a verb.
//
// So the teaching moved into the run. Twice per fresh device the game *stops*
// with the obstacle already on screen and asks for the exact gesture that
// clears it; the gesture that dismisses the overlay IS the action, injected on
// the frozen frame. Nobody is told what will happen — they do it and it works.
//
// Two invariants make that honest rather than a scripted cutscene:
//
// 1. The stop frames are the *perfect bot's* ideal action frames for the first
//    spike/gap and the first bar (`botInputs(obstacles, {})`, reaction 0, error
//    0). Freezing there means a player who simply obeys clears the obstacle
//    with the best timing available — the tutorial can never teach a death.
// 2. The injected action is a real recorded input: it goes into `inputs` on the
//    frozen frame like any thumb press, so the run still replays byte-identical
//    on the server. Tutorial stops record no PauseEvent — they are not a §4.5
//    interruption, they are frames the player simply had longer to think about,
//    and the server has no business seeing them.

import { Action, botInputs, type Obstacle } from '@beatme/sim';

export type TutorialStep = 'jump' | 'slide';

/** Set once a device has been taught; absence of this key is "fresh device". */
const DONE_KEY = 'beatme.tutorialDone';
/** Set by the result-screen link: teach me again on the next run. */
const NEXT_KEY = 'beatme.tutorialNext';

/** Fresh device, or the player asked for the tutorial again on the result screen. */
export function tutorialArmed(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === null || localStorage.getItem(NEXT_KEY) === '1';
  } catch {
    // Private mode / storage blocked: no memory means every run would replay the
    // tutorial. Better to teach nobody than to nag forever.
    return false;
  }
}

/** Result-screen 「下一局重新教学」. */
export function armTutorialNext(): void {
  try {
    localStorage.setItem(NEXT_KEY, '1');
  } catch {
    /* storage blocked — the link is a nicety */
  }
}

function markTutorialDone(): void {
  try {
    localStorage.setItem(DONE_KEY, '1');
    localStorage.removeItem(NEXT_KEY);
  } catch {
    /* storage blocked */
  }
}

interface Stop {
  step: TutorialStep;
  /** Frame the sim is *about* to simulate when the run freezes. */
  frame: number;
}

export class Tutorial {
  /** Ascending by frame; the jump stop always precedes the bar stop (design
   *  §2.3 keeps bars out of the first 15 s). */
  private readonly stops: Stop[];
  private cursor = 0;
  private finished = false;

  constructor(obstacles: readonly Obstacle[]) {
    // The ideal stream for this exact track — the same bot the server uses to
    // bootstrap official ghosts, at its best tier.
    const ideal = botInputs(obstacles, {});
    const jump = ideal.find((e) => e.a === Action.Jump);
    const slide = ideal.find((e) => e.a === Action.SlideStart);
    const stops: Stop[] = [];
    if (jump) stops.push({ step: 'jump', frame: jump.f });
    if (slide) stops.push({ step: 'slide', frame: slide.f });
    stops.sort((a, b) => a.frame - b.frame);
    this.stops = stops;
  }

  /** The step to stop on before simulating `frame`, or null. Stops whose frame
   *  has already gone by (the player died and revived past one, say) are
   *  dropped rather than fired late at the wrong place on the track. */
  stepAt(frame: number): TutorialStep | null {
    while (this.cursor < this.stops.length) {
      const stop = this.stops[this.cursor];
      if (!stop) break;
      if (frame < stop.frame) return null;
      if (frame === stop.frame) return stop.step;
      this.cursor++;
    }
    return null;
  }

  /** The player did the gesture: advance past this stop. */
  consume(): void {
    this.cursor++;
    if (this.cursor >= this.stops.length) this.finish();
  }

  /**
   * Remember that this device has been taught. Called when the last step is
   * done AND from `endRun` regardless of how far the run got: a player who died
   * on obstacle #1 has still met the tutorial, and re-running the whole thing on
   * their next attempt would read as the game not remembering them. The result
   * screen's 「下一局重新教学」 link is the deliberate way back in.
   */
  finish(): void {
    if (this.finished) return;
    this.finished = true;
    markTutorialDone();
  }

  /** Only meaningful for the debug scene handle / tests. */
  get stopFrames(): readonly number[] {
    return this.stops.map((s) => s.frame);
  }
}

/** The action a completed step injects into the frozen frame. */
export function actionForStep(step: TutorialStep): Action {
  return step === 'jump' ? Action.Jump : Action.SlideStart;
}
